import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IPlanPeriod, defaultValue } from 'app/shared/model/plan-period.model';
import { apiOkr } from 'app/config/axios-interceptor';

const initialState: EntityState<IPlanPeriod> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/okr/plan-period';

// Actions

export const getEntities = createAsyncThunk(
  'planPeriod/fetch_entity_list',
  async ({ searchText, page, size, sort, filters }: IQueryParams) => {
    return apiOkr.get<IPlanPeriod[]>(apiUrl, {
      params: {
        searchText,
        page,
        size,
        filters: filters && JSON.stringify(filters),
        sort: sort && sort,
        // cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);



export const getEntity = createAsyncThunk(
  'planPeriod/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return apiOkr.get<IPlanPeriod>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'planPeriod/create_entity',
  async (entity: IPlanPeriod, thunkAPI) => {
    const result = await apiOkr.post<IPlanPeriod>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'planPeriod/update_entity',
  async (entity: IPlanPeriod, thunkAPI) => {
    const result = await apiOkr.put<IPlanPeriod>(`${apiUrl}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const closeOrOpen = createAsyncThunk(
  'planPeriod/close_or_open_planPeriod',
  async (entity: IPlanPeriod, thunkAPI) => {
    const result = await apiOkr.put<IPlanPeriod>(`${apiUrl}/open-or-close`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'planPeriod/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await apiOkr.delete<IPlanPeriod>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const PlanPeriodSlice = createEntitySlice({
  name: 'planPeriod',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.entity = {};
      })
      .addMatcher(isPending(createEntity, updateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = PlanPeriodSlice.actions;

// Reducer
export default PlanPeriodSlice.reducer;
